import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Sprout, Printer, FileDown, GraduationCap, Package, Megaphone, Landmark, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';
import { exportToExcel } from '../../utils/excelExport';

const fmt = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN')}`;
const dateIN = (s) => (s ? new Date(s).toLocaleDateString('en-IN') : '-');

const LivelihoodsReport = () => {
  const { isAdmin } = useAuth();
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/branches').then(r => setBranches(r.data)).catch(() => {}); }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (branch !== 'all') params.branch_id = branch;
      const resp = await api.get('/livelihoods/report', { params });
      setData(resp.data);
    } catch { toast.error('Failed to fetch report'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(); /* eslint-disable-next-line */ }, [branch]);

  const branchName = () => branch === 'all' ? 'All Branches' : (branches.find(b => b.id === branch)?.name || 'Unknown');

  const handlePrint = () => {
    if (!data) return;
    const st = data.skill_training;
    const pr = data.product;
    const mk = data.marketing;
    const fs = data.financial_support;

    const stRows = (st.entries || []).map((e, i) => `<tr style="background:${i%2?'#f9f9f9':'#fff'}"><td>${e.entrepreneur_name || '-'}</td><td>${e.branch_name || '-'}</td><td>${e.training_name || '-'}</td><td>${e.institute || '-'}</td><td>${e.training_duration || '-'}</td><td>${dateIN(e.training_date)}</td><td style="text-align:center">${e.training_status || '-'}</td><td style="text-align:right">${e.earning_before_training ? fmt(e.earning_before_training) : '-'}</td><td style="text-align:right">${e.earning_after_training ? fmt(e.earning_after_training) : '-'}</td><td>${e.employer_name || '-'}</td><td style="text-align:right">${e.monthly_revenue ? fmt(e.monthly_revenue) : '-'}</td><td style="max-width:180px;font-size:10px;color:#334155">${(e.business_address || '-').replace(/</g,'&lt;')}</td><td style="text-align:center">${(e.photos && e.photos.length) ? `${e.photos.length} 📷` : '-'}</td></tr>`).join('');

    const stPhotoBlocks = (st.entries || []).filter(e => e.photos && e.photos.length).map(e => `
      <div style="border:1px solid #e2e8f0;border-radius:6px;padding:8px;margin:8px 0;page-break-inside:avoid">
        <div style="font-size:11px;color:#0f172a;margin-bottom:6px"><b>${e.entrepreneur_name || 'Beneficiary'}</b> — ${e.training_name || 'Training'} · <span style="color:#64748b">${e.branch_name || ''}</span></div>
        ${e.business_address ? `<div style="font-size:10px;color:#475569;margin-bottom:6px">📍 ${e.business_address.replace(/</g,'&lt;')}</div>` : ''}
        <div style="display:flex;gap:6px;flex-wrap:wrap">
          ${(e.photos || []).slice(0,3).map(src => `<img src="${src}" style="width:130px;height:130px;object-fit:cover;border-radius:4px;border:1px solid #cbd5e1" />`).join('')}
        </div>
      </div>
    `).join('');

    const prRows = (pr.entries || []).map((e, i) => `<tr style="background:${i%2?'#f9f9f9':'#fff'}"><td>${e.entrepreneur_name || '-'}</td><td>${e.branch_name || '-'}</td><td>${e.product_name || '-'}</td><td>${e.product_type || '-'}</td><td>${e.quantity || '-'}</td><td>${e.pricing || '-'}</td><td>${e.udyam_registration_no || '-'}</td><td>${e.remarks || '-'}</td></tr>`).join('');

    const mkRows = (mk.entries || []).map((e, i) => `<tr style="background:${i%2?'#f9f9f9':'#fff'}"><td>${e.entrepreneur_name || '-'}</td><td>${e.branch_name || '-'}</td><td>${e.activity_name || '-'}</td><td>${e.activity_type || '-'}</td><td>${dateIN(e.event_date)}</td><td>${e.description || '-'}</td></tr>`).join('');

    const fsRows = (fs.entries || []).map((e, i) => `<tr style="background:${i%2?'#f9f9f9':'#fff'}"><td>${e.entrepreneur_name || '-'}</td><td>${e.branch_name || '-'}</td><td>${e.institution_name || '-'}</td><td>${e.institution_type || '-'}</td><td style="text-align:right">${fmt(e.support_amount)}</td><td>${dateIN(e.support_date)}</td><td style="text-align:center">${e.support_status || '-'}</td></tr>`).join('');

    const body = `
      <div class="cards">
        <div class="card"><small>Total Entries</small><b>${data.total_entries}</b></div>
        <div class="card"><small>Unique Beneficiaries</small><b>${data.unique_entrepreneurs}</b></div>
        <div class="card"><small>Trainings</small><b>${st.total}</b></div>
        <div class="card"><small>Products</small><b>${pr.total}</b></div>
        <div class="card"><small>Marketing</small><b>${mk.total}</b></div>
        <div class="card"><small>Financial Support</small><b>${fs.total}</b></div>
      </div>

      <h3>1. Skill & Job Training (${st.total})</h3>
      <div class="cards">
        <div class="card"><small>Completed</small><b>${st.completed}</b></div>
        <div class="card"><small>Ongoing</small><b>${st.ongoing}</b></div>
        <div class="card"><small>Dropped</small><b>${st.dropped}</b></div>
        <div class="card"><small>Planned</small><b>${st.planned}</b></div>
        <div class="card"><small>With Job</small><b>${st.with_job}</b></div>
        <div class="card"><small>Self-Employed</small><b>${st.self_employed}</b></div>
        <div class="card"><small>Total Earning Before</small><b>${fmt(st.earning_before_total)}</b></div>
        <div class="card"><small>Total Earning After</small><b>${fmt(st.earning_after_total)}</b></div>
        <div class="card"><small>Est. Annual Revenue</small><b>${fmt(st.annual_revenue_sum)}</b></div>
      </div>
      ${st.total ? `<table><thead><tr><th>Entrepreneur</th><th>Branch</th><th>Training</th><th>Institute</th><th>Duration</th><th>Date</th><th>Status</th><th>Before</th><th>After</th><th>Employer</th><th>Monthly Rev.</th><th>Business Address</th><th>Photos</th></tr></thead><tbody>${stRows}</tbody></table>${stPhotoBlocks ? `<h4 style="margin-top:14px">Business / Training Photographs</h4>${stPhotoBlocks}` : ''}` : '<p style="color:#666;font-style:italic">No training entries found.</p>'}

      <h3>2. Products (${pr.total})</h3>
      <div class="cards">
        <div class="card"><small>Total Products</small><b>${pr.total}</b></div>
        <div class="card"><small>MSME / UDYAM Registered</small><b>${pr.msme_registered}</b></div>
        ${Object.entries(pr.by_type||{}).map(([k,v])=>`<div class="card"><small>${k}</small><b>${v}</b></div>`).join('')}
      </div>
      ${pr.total ? `<table><thead><tr><th>Entrepreneur</th><th>Branch</th><th>Product</th><th>Type</th><th>Quantity</th><th>Pricing</th><th>UDYAM/MSME No.</th><th>Remarks</th></tr></thead><tbody>${prRows}</tbody></table>` : '<p style="color:#666;font-style:italic">No product entries found.</p>'}

      <h3>3. Marketing & Promotion (${mk.total})</h3>
      <div class="cards">
        <div class="card"><small>Total Activities</small><b>${mk.total}</b></div>
        ${Object.entries(mk.by_type||{}).map(([k,v])=>`<div class="card"><small>${k}</small><b>${v}</b></div>`).join('')}
      </div>
      ${mk.total ? `<table><thead><tr><th>Entrepreneur</th><th>Branch</th><th>Activity</th><th>Type</th><th>Date</th><th>Description</th></tr></thead><tbody>${mkRows}</tbody></table>` : '<p style="color:#666;font-style:italic">No marketing entries found.</p>'}

      <h3>4. Financial Support / Linkage (${fs.total})</h3>
      <div class="cards">
        <div class="card"><small>Applied</small><b>${fmt(fs.amount_applied)}</b></div>
        <div class="card"><small>Sanctioned</small><b>${fmt(fs.amount_sanctioned)}</b></div>
        <div class="card"><small>Disbursed</small><b>${fmt(fs.amount_disbursed)}</b></div>
        ${Object.entries(fs.by_institution_type||{}).map(([k,v])=>`<div class="card"><small>${k}</small><b>${v.count} | ${fmt(v.amount)}</b></div>`).join('')}
      </div>
      ${fs.total ? `<table><thead><tr><th>Entrepreneur</th><th>Branch</th><th>Institution</th><th>Type</th><th>Amount</th><th>Date</th><th>Status</th></tr></thead><tbody>${fsRows}</tbody></table>` : '<p style="color:#666;font-style:italic">No financial support entries found.</p>'}
    `;
    openPrintWindow(buildPrintHTML('Livelihoods Cell — Consolidated Report', body, branchName()));
  };

  const handleExcel = () => {
    if (!data) return;
    const skillRows = (data.skill_training.entries || []).map((e, i) => ({
      'SN': i + 1,
      'Entrepreneur': e.entrepreneur_name || '',
      'Branch': e.branch_name || '',
      'Training Name': e.training_name || '',
      'Institute': e.institute || '',
      'Duration': e.training_duration || '',
      'Training Date': dateIN(e.training_date),
      'Status': e.training_status || '',
      'Earning Before': Math.round(e.earning_before_training || 0),
      'Earning After': Math.round(e.earning_after_training || 0),
      'Employer': e.employer_name || '',
      'Monthly Revenue': Math.round(e.monthly_revenue || 0),
      'Business Address': e.business_address || '',
      'Photos': (e.photos && e.photos.length) ? `${e.photos.length} image(s)` : '',
      'Remarks': e.remarks || '',
    }));
    const productRows = (data.product.entries || []).map((e, i) => ({
      'SN': i + 1,
      'Entrepreneur': e.entrepreneur_name || '',
      'Branch': e.branch_name || '',
      'Product Name': e.product_name || '',
      'Product Type': e.product_type || '',
      'Quantity': e.quantity || '',
      'Pricing': e.pricing || '',
      'UDYAM/MSME No.': e.udyam_registration_no || '',
      'Remarks': e.remarks || '',
    }));
    const marketingRows = (data.marketing.entries || []).map((e, i) => ({
      'SN': i + 1,
      'Entrepreneur': e.entrepreneur_name || '',
      'Branch': e.branch_name || '',
      'Activity Name': e.activity_name || '',
      'Activity Type': e.activity_type || '',
      'Event Date': dateIN(e.event_date),
      'Description': e.description || '',
      'Remarks': e.remarks || '',
    }));
    const finRows = (data.financial_support.entries || []).map((e, i) => ({
      'SN': i + 1,
      'Entrepreneur': e.entrepreneur_name || '',
      'Branch': e.branch_name || '',
      'Institution Name': e.institution_name || '',
      'Institution Type': e.institution_type || '',
      'Support Amount': Math.round(e.support_amount || 0),
      'Support Date': dateIN(e.support_date),
      'Status': e.support_status || '',
      'Remarks': e.remarks || '',
    }));
    const summaryRows = [
      { Metric: 'Branch', Value: branchName() },
      { Metric: 'Total Entries', Value: data.total_entries },
      { Metric: 'Unique Beneficiaries', Value: data.unique_entrepreneurs },
      { Metric: 'Skill Trainings', Value: data.skill_training.total },
      { Metric: 'Products', Value: data.product.total },
      { Metric: 'Marketing Activities', Value: data.marketing.total },
      { Metric: 'Financial Support', Value: data.financial_support.total },
      { Metric: 'Financial Applied', Value: Math.round(data.financial_support.amount_applied || 0) },
      { Metric: 'Financial Sanctioned', Value: Math.round(data.financial_support.amount_sanctioned || 0) },
      { Metric: 'Financial Disbursed', Value: Math.round(data.financial_support.amount_disbursed || 0) },
    ];
    exportToExcel({
      filename: `WWAGS_Livelihoods_${branchName().replace(/\s+/g,'_')}`,
      sheets: [
        { name: 'Summary', rows: summaryRows },
        { name: 'Skill Training', rows: skillRows },
        { name: 'Products', rows: productRows },
        { name: 'Marketing', rows: marketingRows },
        { name: 'Financial Support', rows: finRows },
      ],
    });
  };

  const handleCsv = () => {
    if (!data) return;
    const rows = [['Category','Entrepreneur','Branch','Name/Title','Type','Status/Date','Amount/Earning','Institute/Employer/Institution','UDYAM/MSME','Remarks']];
    (data.skill_training.entries || []).forEach(e => rows.push(['Skill Training', e.entrepreneur_name||'', e.branch_name||'', e.training_name||'', '', e.training_status||'', `Before ${e.earning_before_training||0} / After ${e.earning_after_training||0} / Rev ${e.monthly_revenue||0}`, e.institute||e.employer_name||'', '', e.remarks||'']));
    (data.product.entries || []).forEach(e => rows.push(['Product', e.entrepreneur_name||'', e.branch_name||'', e.product_name||'', e.product_type||'', '', `${e.quantity||''} @ ${e.pricing||''}`, '', e.udyam_registration_no||'', e.remarks||'']));
    (data.marketing.entries || []).forEach(e => rows.push(['Marketing', e.entrepreneur_name||'', e.branch_name||'', e.activity_name||'', e.activity_type||'', e.event_date||'', '', e.description||'', '', e.remarks||'']));
    (data.financial_support.entries || []).forEach(e => rows.push(['Financial Support', e.entrepreneur_name||'', e.branch_name||'', e.institution_name||'', e.institution_type||'', `${e.support_status||''} ${e.support_date||''}`, e.support_amount||0, '', '', e.remarks||'']));

    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Livelihoods_Report_${branchName().replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="livelihoods-report-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Livelihoods Cell — Consolidated Report</h1>
          <p className="text-sm text-slate-600">All entries from Skill Training, Products, Marketing & Financial Support in one view</p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        {isAdmin() && (
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger className="w-56" data-testid="branch-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button onClick={fetchReport} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="refresh-btn">Refresh</Button>
        {data && <Button variant="outline" onClick={handlePrint} data-testid="print-btn"><Printer className="w-4 h-4 mr-1" />Print / PDF</Button>}
        {data && <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>}
        {data && <Button variant="outline" onClick={handleCsv} data-testid="csv-btn"><FileDown className="w-4 h-4 mr-1" />Download CSV</Button>}
      </div>

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div></div>}

      {data && !loading && (
        <div className="space-y-6">
          {/* Top-level Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            <Kpi label="Total Entries" value={data.total_entries} color="slate" />
            <Kpi label="Unique Beneficiaries" value={data.unique_entrepreneurs} color="emerald" />
            <Kpi label="Trainings" value={data.skill_training.total} color="emerald" />
            <Kpi label="Products" value={data.product.total} color="blue" />
            <Kpi label="Marketing" value={data.marketing.total} color="purple" />
            <Kpi label="Financial Support" value={data.financial_support.total} color="amber" />
          </div>

          {/* Section 1 - Skill Training */}
          <Section title="1. Skill & Job Training" icon={GraduationCap} color="emerald" count={data.skill_training.total}>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
              <Chip label="Completed" value={data.skill_training.completed} color="emerald" />
              <Chip label="Ongoing" value={data.skill_training.ongoing} color="blue" />
              <Chip label="Planned" value={data.skill_training.planned} color="slate" />
              <Chip label="Dropped" value={data.skill_training.dropped} color="red" />
              <Chip label="Got Job" value={data.skill_training.with_job} color="purple" />
              <Chip label="Self-Employed" value={data.skill_training.self_employed} color="amber" />
              <Chip label="Total Before" value={fmt(data.skill_training.earning_before_total)} color="slate" />
              <Chip label="Total After" value={fmt(data.skill_training.earning_after_total)} color="emerald" />
              <Chip label="Est. Annual Rev." value={fmt(data.skill_training.annual_revenue_sum)} color="blue" />
            </div>
            <TableWrapper>
              <table className="w-full text-xs">
                <thead className="bg-emerald-700 text-white"><tr>
                  <th className="px-3 py-2 text-left">Entrepreneur</th><th className="px-3 py-2 text-left">Branch</th><th className="px-3 py-2 text-left">Training</th><th className="px-3 py-2 text-left">Institute</th><th className="px-3 py-2 text-left">Duration</th><th className="px-3 py-2 text-center">Date</th><th className="px-3 py-2 text-center">Status</th><th className="px-3 py-2 text-right">Before</th><th className="px-3 py-2 text-right">After</th><th className="px-3 py-2 text-left">Employer</th><th className="px-3 py-2 text-right">Monthly Rev.</th><th className="px-3 py-2 text-left">Business Address</th><th className="px-3 py-2 text-center">Photos</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.skill_training.entries.length === 0 ? (
                    <tr><td colSpan="13" className="text-center py-6 text-slate-400 italic">No skill training entries.</td></tr>
                  ) : data.skill_training.entries.map((e, i) => (
                    <tr key={e.id} className={i%2?'bg-slate-50':''}>
                      <td className="px-3 py-2">{e.entrepreneur_id ? <Link to={`/entrepreneurs/${e.entrepreneur_id}`} className="text-blue-700 hover:underline">{e.entrepreneur_name}</Link> : '-'}</td>
                      <td className="px-3 py-2">{e.branch_name || '-'}</td>
                      <td className="px-3 py-2 font-medium">{e.training_name || '-'}</td>
                      <td className="px-3 py-2">{e.institute || '-'}</td>
                      <td className="px-3 py-2">{e.training_duration || '-'}</td>
                      <td className="px-3 py-2 text-center">{dateIN(e.training_date)}</td>
                      <td className="px-3 py-2 text-center">{e.training_status || '-'}</td>
                      <td className="px-3 py-2 text-right">{e.earning_before_training ? fmt(e.earning_before_training) : '-'}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-medium">{e.earning_after_training ? fmt(e.earning_after_training) : '-'}</td>
                      <td className="px-3 py-2">{e.employer_name || '-'}</td>
                      <td className="px-3 py-2 text-right">{e.monthly_revenue ? fmt(e.monthly_revenue) : '-'}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate" title={e.business_address || ''}>{e.business_address || '-'}</td>
                      <td className="px-3 py-2 text-center">
                        {e.photos && e.photos.length > 0 ? (
                          <div className="flex gap-0.5 justify-center">
                            {e.photos.slice(0, 3).map((src, pi) => (
                              <a key={pi} href={src} target="_blank" rel="noreferrer" title={`Photo ${pi + 1}`}>
                                <img src={src} alt="" className="w-8 h-8 object-cover rounded border border-slate-300 hover:ring-2 hover:ring-emerald-500 cursor-zoom-in" />
                              </a>
                            ))}
                          </div>
                        ) : <span className="text-slate-400">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrapper>
          </Section>

          {/* Section 2 - Products */}
          <Section title="2. Products" icon={Package} color="blue" count={data.product.total}>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
              <Chip label="Total" value={data.product.total} color="blue" />
              <Chip label="MSME Registered" value={data.product.msme_registered} color="emerald" />
              {Object.entries(data.product.by_type || {}).map(([k, v]) => <Chip key={k} label={k} value={v} color="slate" />)}
            </div>
            <TableWrapper>
              <table className="w-full text-xs">
                <thead className="bg-blue-700 text-white"><tr>
                  <th className="px-3 py-2 text-left">Entrepreneur</th><th className="px-3 py-2 text-left">Branch</th><th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-center">Type</th><th className="px-3 py-2 text-center">Quantity</th><th className="px-3 py-2 text-right">Pricing</th><th className="px-3 py-2 text-left">UDYAM/MSME</th><th className="px-3 py-2 text-left">Remarks</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.product.entries.length === 0 ? (
                    <tr><td colSpan="8" className="text-center py-6 text-slate-400 italic">No product entries.</td></tr>
                  ) : data.product.entries.map((e, i) => (
                    <tr key={e.id} className={i%2?'bg-slate-50':''}>
                      <td className="px-3 py-2">{e.entrepreneur_id ? <Link to={`/entrepreneurs/${e.entrepreneur_id}`} className="text-blue-700 hover:underline">{e.entrepreneur_name}</Link> : '-'}</td>
                      <td className="px-3 py-2">{e.branch_name || '-'}</td>
                      <td className="px-3 py-2 font-medium">{e.product_name}</td>
                      <td className="px-3 py-2 text-center">{e.product_type || '-'}</td>
                      <td className="px-3 py-2 text-center">{e.quantity || '-'}</td>
                      <td className="px-3 py-2 text-right">{e.pricing || '-'}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{e.udyam_registration_no || <span className="text-slate-400 italic">Not registered</span>}</td>
                      <td className="px-3 py-2 text-slate-500">{e.remarks || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrapper>
          </Section>

          {/* Section 3 - Marketing */}
          <Section title="3. Marketing & Promotion" icon={Megaphone} color="purple" count={data.marketing.total}>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
              <Chip label="Total Activities" value={data.marketing.total} color="purple" />
              {Object.entries(data.marketing.by_type || {}).map(([k, v]) => <Chip key={k} label={k} value={v} color="slate" />)}
            </div>
            <TableWrapper>
              <table className="w-full text-xs">
                <thead className="bg-purple-700 text-white"><tr>
                  <th className="px-3 py-2 text-left">Entrepreneur</th><th className="px-3 py-2 text-left">Branch</th><th className="px-3 py-2 text-left">Activity</th><th className="px-3 py-2 text-center">Type</th><th className="px-3 py-2 text-center">Date</th><th className="px-3 py-2 text-left">Description</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.marketing.entries.length === 0 ? (
                    <tr><td colSpan="6" className="text-center py-6 text-slate-400 italic">No marketing entries.</td></tr>
                  ) : data.marketing.entries.map((e, i) => (
                    <tr key={e.id} className={i%2?'bg-slate-50':''}>
                      <td className="px-3 py-2">{e.entrepreneur_id ? <Link to={`/entrepreneurs/${e.entrepreneur_id}`} className="text-blue-700 hover:underline">{e.entrepreneur_name}</Link> : '-'}</td>
                      <td className="px-3 py-2">{e.branch_name || '-'}</td>
                      <td className="px-3 py-2 font-medium">{e.activity_name}</td>
                      <td className="px-3 py-2 text-center">{e.activity_type || '-'}</td>
                      <td className="px-3 py-2 text-center">{dateIN(e.event_date)}</td>
                      <td className="px-3 py-2 text-slate-600">{e.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrapper>
          </Section>

          {/* Section 4 - Financial Support */}
          <Section title="4. Financial Support / Linkage" icon={Landmark} color="amber" count={data.financial_support.total}>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
              <Chip label="Applied (Amt)" value={fmt(data.financial_support.amount_applied)} color="amber" />
              <Chip label="Sanctioned (Amt)" value={fmt(data.financial_support.amount_sanctioned)} color="blue" />
              <Chip label="Disbursed (Amt)" value={fmt(data.financial_support.amount_disbursed)} color="emerald" />
              {Object.entries(data.financial_support.by_institution_type || {}).map(([k, v]) => (
                <Chip key={k} label={k} value={`${v.count} | ${fmt(v.amount)}`} color="slate" />
              ))}
            </div>
            <TableWrapper>
              <table className="w-full text-xs">
                <thead className="bg-amber-600 text-white"><tr>
                  <th className="px-3 py-2 text-left">Entrepreneur</th><th className="px-3 py-2 text-left">Branch</th><th className="px-3 py-2 text-left">Institution</th><th className="px-3 py-2 text-center">Type</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-center">Date</th><th className="px-3 py-2 text-center">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.financial_support.entries.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-6 text-slate-400 italic">No financial support entries.</td></tr>
                  ) : data.financial_support.entries.map((e, i) => (
                    <tr key={e.id} className={i%2?'bg-slate-50':''}>
                      <td className="px-3 py-2">{e.entrepreneur_id ? <Link to={`/entrepreneurs/${e.entrepreneur_id}`} className="text-blue-700 hover:underline">{e.entrepreneur_name}</Link> : '-'}</td>
                      <td className="px-3 py-2">{e.branch_name || '-'}</td>
                      <td className="px-3 py-2 font-medium">{e.institution_name}</td>
                      <td className="px-3 py-2 text-center">{e.institution_type || '-'}</td>
                      <td className="px-3 py-2 text-right font-bold">{fmt(e.support_amount)}</td>
                      <td className="px-3 py-2 text-center">{dateIN(e.support_date)}</td>
                      <td className="px-3 py-2 text-center">{e.support_status || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrapper>
          </Section>
        </div>
      )}

      {!data && !loading && (
        <div className="text-center py-16 text-slate-500">
          <Sprout className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>Loading Livelihoods report...</p>
        </div>
      )}
    </div>
  );
};

const Kpi = ({ label, value, color }) => {
  const map = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
  };
  return (
    <div className={`rounded-lg border p-3 ${map[color]}`}>
      <p className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</p>
      <p className="text-xl font-bold mt-1">{value}</p>
    </div>
  );
};

const Chip = ({ label, value, color }) => {
  const map = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-800',
  };
  return (
    <div className={`rounded-md border px-2.5 py-1.5 ${map[color]}`}>
      <p className="text-[10px] font-semibold opacity-80 truncate">{label}</p>
      <p className="text-sm font-bold truncate">{value}</p>
    </div>
  );
};

const Section = ({ title, icon: Icon, color, count, children }) => {
  const map = {
    emerald: 'text-emerald-700 border-emerald-200',
    blue: 'text-blue-700 border-blue-200',
    purple: 'text-purple-700 border-purple-200',
    amber: 'text-amber-700 border-amber-200',
  };
  return (
    <div className={`bg-white rounded-xl border ${map[color]} shadow-sm p-4 md:p-5`}>
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
        <Icon className={`w-5 h-5 ${map[color]}`} />
        <h2 className={`text-base md:text-lg font-bold ${map[color]}`}>{title}</h2>
        <span className="ml-auto text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-medium">{count} entries</span>
      </div>
      {children}
    </div>
  );
};

const TableWrapper = ({ children }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200">{children}</div>
);

export default LivelihoodsReport;
